import { CALCULATION_TYPES } from './constants'

/**
 * Merges global date settings as fallback into column options.
 * If the column has dateMode 'default' (or no dateMode), inherit the global date config.
 * Otherwise the column's own date settings take precedence.
 */
function resolveColumnOptions (colOptions, globalDateConfig) {
  const mode = colOptions?.dateMode
  if (mode && mode !== 'default') return colOptions

  return {
    ...colOptions,
    dateMode: globalDateConfig.dateMode,
    quickDate: globalDateConfig.quickDate,
    startDate: globalDateConfig.startDate,
    endDate: globalDateConfig.endDate
  }
}

function buildCalcs (columns, globalDateConfig) {
  return columns.map(col => {
    const typeDef = CALCULATION_TYPES[col.type]
    if (!typeDef) return null
    const options = resolveColumnOptions(col.options, globalDateConfig)
    return typeDef.buildCalculation(col.id, options)
  }).filter(Boolean)
}

function nestGroupings (groupings, calcs) {
  return [
    groupings.reduceRight((innerCalcs, grouping, index) => [{
      id: `grouping_${index}_${grouping}`,
      $type: 'grouping',
      grouping,
      calculations: [...calcs, ...innerCalcs]
    }], [])[0]
  ]
}

/**
 * Builds the Orion Reporting/Scope request body from user config.
 * Includes entity/entityIds for Orion scoping. The frontend owns entity resolution.
 */
export function buildOrionRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate }, { asOfDate, maxGroupingDepth, entity, entityIds }) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const calcs = buildCalcs(columns, globalDateConfig)
  const effectiveGroupings = maxGroupingDepth != null
    ? groupings.slice(0, maxGroupingDepth)
    : groupings

  const request = { entity, entityIds, asOfDate }

  if (effectiveGroupings.length === 0) {
    request.calculations = calcs
  } else {
    request.calculations = nestGroupings(effectiveGroupings, calcs)
  }

  return request
}

/**
 * Builds a child request for lazy expand — uses remaining groupings
 * starting from the given depth index, scoped to a specific group entity.
 */
export function buildChildRequest ({ groupings, columns, dateMode, quickDate, startDate, endDate }, { asOfDate, depthIndex, entity, entityIds }) {
  const globalDateConfig = { dateMode, quickDate, startDate, endDate }
  const calcs = buildCalcs(columns, globalDateConfig)
  const remainingGroupings = groupings.slice(depthIndex)

  const request = { entity, entityIds, asOfDate }

  if (remainingGroupings.length === 0) {
    request.calculations = calcs
  } else {
    request.calculations = nestGroupings(remainingGroupings, calcs)
  }

  return request
}
